import { defineStore } from 'pinia';
import { storeConfig } from '@/shared/storeConfig';

import type {
  UpdateUser,
  User,
  UpdatePixKey,
  PixKeyData,
  MexBankAccountData,
  UpdateUserEmail,
} from '@/types/UserData';

export const useUserDataStore = defineStore(
  'userData',
  () => {
    const { ApiService } = useApiService();
    const authStore = useAuthStore();
    const { fetchUserProfile } = useUserData();

    const userIsAuthenticated = computed(() => {
      return authStore.isAuthenticated;
    });

    const user = ref<User | null>(null);

    const zendeskUserToken = ref<string>('');

    const temporaryPassword = ref<string>('');

    const pixKey = ref<PixKeyData>({} as PixKeyData);
    const pixLoading = ref<boolean>(false);

    const userMexBankAccount = ref<MexBankAccountData>(
      {} as MexBankAccountData,
    );

    const userMexBankAccountLoading = ref<boolean>(false);

    const isLoading = ref<boolean>(false);

    const loadedUser = ref<boolean | number>(false);

    function setUserData(data: any) {
      user.value = data;
    }

    function setUserAddress(data: any) {
      if (!user.value) return;

      user.value.address = data.street;
      user.value.city = data.city;
      user.value.state = data.state;
      user.value.zipcode = data.zipcode;
    }

    function setLoadedUser() {
      loadedUser.value = true;
    }

    function $reset() {
      user.value = null;
      zendeskUserToken.value = '';
      pixKey.value = {} as PixKeyData;
      pixLoading.value = false;
    }

    function setLoading(data: boolean) {
      isLoading.value = data;
    }

    function delayToFixBug(time: number) {
      return new Promise(function (resolve: any) {
        setTimeout(function () {
          resolve(true);
        }, time);
      });
    }

    async function updateUserAccount(id: number, data: UpdateUser) {
      return await ApiService.post(`/users/update/${id}`, data);
    }

    async function updateUserContract(type: string) {
      return await ApiService.patch(`/bff/users/self-contracts`, { type });
    }

    async function updateUserDocumentCPF(data: any) {
      return await ApiService.post(`documents/store-user-document`, data);
    }

    async function updateUserDocumentCURP(data: any) {
      return await ApiService.post(`documents/store-mex-document`, data);
    }

    async function updateUserEmail(payload: UpdateUserEmail) {
      return await ApiService.patch(`/bff/users/update-email`, payload);
    }

    async function getUserPixKey() {
      try {
        pixLoading.value = true;

        const { data } = await ApiService.post('/pix-keys/user-key', {});

        pixKey.value = {
          value: data.pix_key,
          type: data.pix_key_type,
        };

        pixLoading.value = false;
      } catch (err) {
        console.error({ err });
        pixLoading.value = false;
      }
    }

    async function updatePixKey(data: {
      pix_key: string;
      pix_key_type: string;
    }) {
      const update = await ApiService.post('/pix-keys/update-user-key', data);

      if (update.data.success) {
        pixKey.value = {
          value: data.pix_key,
          type: data.pix_key_type,
        };
      }

      return update;
    }

    async function updatePixKeyV2(data: UpdatePixKey) {
      const update = await ApiService.post(
        '/pix-keys/update-user-key-v2',
        data,
      );

      if (update.data.success) {
        pixKey.value = {
          value: data.pix_key_value,
          type: data.pix_key_type,
        };
      }

      return update;
    }

    async function getUserMexBankAccount() {
      try {
        userMexBankAccountLoading.value = true;

        const { data } = await ApiService.get(
          '/mex-bank-accounts/user-account',
        );

        userMexBankAccount.value = {
          type: data.type,
          number: data.number,
          bank_code: data.bank_code,
        };

        userMexBankAccountLoading.value = false;
      } catch (err) {
        console.error({ err });
        userMexBankAccountLoading.value = false;
      }
    }

    async function updateUserMexBankAccount(data: MexBankAccountData) {
      const update = await ApiService.post('/mex-bank-accounts/store', data);

      if (update.data.bank_account) {
        getUserMexBankAccount();
      }

      return update;
    }

    function treatVariable(variable: string): string {
      const treatedVariable = variable
        .normalize('NFD')
        .replace(/[\u0300-\u036F]/g, '')
        .replace(/[^a-zA-Z0-9\s]/g, '');

      return treatedVariable;
    }

    function getAccountHeaders(): {
      country: string;
      country_alpha3: string;
      currency: string;
      city: string;
      jurisdiction: string;
    } {
      return {
        country: user.value?.country_data?.alpha2 ?? '',
        country_alpha3: user.value?.country_data?.code ?? '',
        currency: user.value?.currency ?? '',
        city: user.value?.city ? treatVariable(user.value?.city) : '',
        jurisdiction: user.value?.country_data?.alpha2 ?? '',
      };
    }

    async function checkHasUserData() {
      let haveUser: any = false;
      let isLogged = userIsAuthenticated.value;

      await delayToFixBug(1);

      try {
        haveUser = !!(user && user.value && user.value.id);
      } catch (ex) {}

      if (!isLogged) {
        isLogged = userIsAuthenticated.value;
      }

      if (isLogged && (!haveUser || !loadedUser.value)) {
        await fetchUserProfile();
      }
    }

    checkHasUserData();

    return {
      user,
      temporaryPassword,
      setUserData,
      setUserAddress,
      updateUserAccount,
      updateUserContract,
      updateUserDocumentCPF,
      updateUserDocumentCURP,
      updateUserEmail,
      treatVariable,
      getAccountHeaders,
      $reset,
      isLoading,
      setLoading,
      setLoadedUser,
      loadedUser,
      pixKey,
      pixLoading,
      getUserPixKey,
      updatePixKey,
      updatePixKeyV2,
      userMexBankAccount,
      userMexBankAccountLoading,
      getUserMexBankAccount,
      updateUserMexBankAccount,
      zendeskUserToken,
    };
  },

  storeConfig,
);
